import React, { useContext, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import queryString from 'query-string';
import { useLocation, useHistory } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import { Typography, Paper, Grid, Box } from '@material-ui/core/';

import { ctx } from 'store';
import EmptyTrunk from 'components/animations/EmptyTrunk';
import ActivityList from 'components/ActivityList';
import Dropdown from 'components/Dropdown';
import Loading from 'components/animations/Loading';
import useSenslaApi from 'hooks/useSenslaApi';

// Counters
import ActiveMembersCounter from '../counters/ActiveMembersCounter';
import ActivityCounter from '../counters/ActivityCounter';
import ChannelsCounter from '../counters/ChannelsCounter';
import MoodCounter from '../counters/MoodCounter';
import MentionsCounter from '../counters/MentionsCounter';

import { useStyles } from './styles';

const ALL_CHANNELS = 'All-channels';
const ALL_GROUPS = 'All-teams';

const Summary: React.FC = () => {
  const {
    teams: { currentTeam },
  } = useContext(ctx.store);
  const location = useLocation();
  const history = useHistory();
  const classes = useStyles();
  const [selectedChannel, setSelectedChannel] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [daysSelected, setDaysSelected] = useState(1);
  const [channels, setChannels] = useState<{ id: string }[]>([]);
  const [groups, setGroups] = useState<{ id: string }[]>([]);
  const queryParams: any = queryString.parse(location.search);
  const { channelId, groupId } = queryParams;
  const [channel, setChannel] = useState(channelId ? channelId : ALL_CHANNELS);
  const [group, setGroup] = useState(groupId ? groupId : ALL_GROUPS);
  const query = queryString.stringify(
    {
      days: daysSelected,
      channel: channel === ALL_CHANNELS ? null : channel,
      group: group === ALL_GROUPS ? null : group,
      team: currentTeam.teamId ? currentTeam.teamId : currentTeam.tenantId,
      platform: currentTeam.teamId ? 'slack' : 'microsoft',
    },
    {
      skipNull: true,
    }
  );

  const [{ data: activityReport, loading }] = useSenslaApi(
    `activities/report/summary?${query}`,
    'GET',
    true
  );

  useEffect(() => {
    const data = [
      ...(currentTeam.teamId ? currentTeam.channels : currentTeam.msChannels)
        .map((ch: any) => {
          if (ch.monitored) {
            return { id: ch.channelId };
          }
        })
        .filter((c: any) => c),
    ];
    const groupsData = [
      ...(currentTeam.teamId ? [] : currentTeam.msGroups)
        .filter((gr: any) => gr.monitored)
        .map((group: any) => {
          return { id: group.groupId };
        }),
    ];
    onChange(channelId, 'channels', true);
    setSelectedChannel(channelId);
    setChannels(data);
    if (!currentTeam.teamId) {
      onChange(groupId, 'teams', true);
      setSelectedGroup(groupId);
      setGroups(groupsData);
    }
    setDaysSelected(1);
  }, []);

  const mostActiveMembers = useMemo(() => {
    if (!activityReport) return [];
    return activityReport.memberActivity
      .sort((m1: any, m2: any) => m2.count - m1.count)
      .filter(
        (mem: any) =>
          mem.member &&
          !mem.member.isBot &&
          !mem.member.isRestricted &&
          !mem.member.deleted &&
          mem.member.memberId !== 'USLACKBOT'
      )
      .map((m: any) => ({
        member: m.member.memberId,
        username: m.member.username,
        count: m.count,
        score: m.avg,
      }));
  }, [activityReport]);

  const mostActiveChannels = useMemo(() => {
    if (!activityReport) return [];
    return activityReport.channelActivity
      .sort((m1: any, m2: any) => m2.count - m1.count)
      .filter((ch: any) => ch.channel)
      .map((c: any) => ({
        channel: c.channel.channelId,
        name: c.channel.name,
        count: c.count,
        score: c.avg,
      }));
  }, [activityReport]);

  const membersWithoutActivity = useMemo(() => {
    if (!activityReport) return [];
    const activeMembers = activityReport.memberActivity
      .filter(
        (mem: any) => mem.member && !mem.isBot && !mem.member.deleted && !mem.member.isRestricted
      )
      .map((m: any) => m.member.memberId);
    return (currentTeam.teamId ? currentTeam.members : currentTeam.msMembers)
      .filter(
        (m: any) =>
          !activeMembers.includes(m.memberId) &&
          !m.isBot &&
          !m.isRestricted &&
          !m.deleted &&
          m.memberId !== 'USLACKBOT' &&
          (channel === ''
            ? true
            : (currentTeam.teamId ? currentTeam.channels : currentTeam.msChannels)
                .filter((ch: any) => ch.channelId === channel)[0]
                .members.includes(m.memberId))
      )
      .map((m: any) => ({ member: m.memberId, username: m.username }));
  }, [activityReport]);

  const mostProblematicMembers = useMemo(() => {
    if (!activityReport) return [];
    return activityReport.memberActivity
      .sort((m1: any, m2: any) => m1.avg - m2.avg)
      .filter(
        (mem: any) =>
          mem.member &&
          mem.member.count > 1 &&
          !mem.member.isBot &&
          !mem.member.isRestricted &&
          !mem.member.deleted &&
          mem.member.memberId !== 'USLACKBOT'
      )
      .map((m: any) => ({
        member: m.member.memberId,
        username: m.member.username,
        score: m.avg,
        count: m.count,
      }));
  }, [activityReport]);

  // Counters
  const onChange = (value: string, type: string, fromEffect?: boolean) => {
    if (!fromEffect) {
      let groupQuery =
        group || type === 'teams' ? `groupId=${type === 'teams' ? value : group}` : '';
      let channelQuery =
        channel || type === 'channels' ? `channelId=${type === 'channels' ? value : channel}` : '';

      groupQuery = groupQuery === 'groupId=All-teams' ? '' : groupQuery;
      channelQuery = channelQuery === 'channelId=All-channels' ? '' : channelQuery;

      let completeQuery;
      if (groupQuery !== '' && channelQuery !== '') {
        completeQuery = `?${groupQuery}&${channelQuery}`;
      } else if (groupQuery === '') {
        completeQuery = `?${channelQuery}`;
      } else if (channelQuery === '') {
        completeQuery = `?${groupQuery}`;
      }

      history.push(`/dashboard/summary${completeQuery}`);
    }

    if (type === 'channels') {
      setSelectedChannel(value);
      const filteredChannel = (currentTeam.teamId
        ? currentTeam.channels
        : currentTeam.msChannels
      ).filter((channel: any) => channel.channelId === value)[0];
      if (filteredChannel) {
        setChannel(filteredChannel.channelId);
      } else {
        setChannel('');
      }
    } else if (type === 'teams') {
      setSelectedGroup(value);
      const filteredGroup = currentTeam.msGroups.filter((group: any) => group.groupId === value)[0];
      if (filteredGroup) {
        setGroup(filteredGroup.groupId);
      } else {
        setGroup('');
      }
    }
  };

  const dropdownChannels = useMemo(() => {
    if (!groupId || groupId === '') {
      return channels;
    }
    return currentTeam.msGroups
      .find((group: any) => group.groupId === groupId)
      .channels.map((channel: any) => ({ id: channel }));
  }, [groupId, channels]);

  return (
    <div style={{ height: '100vh' }}>
      <Box display="flex" p={1} alignItems="center" className={classes.header}>
        <Box p={1} flexGrow={1}>
          <Typography component="h1" variant="h5">
            24 Hours Summary
          </Typography>
        </Box>
        {!currentTeam.teamId ? (
          <Box p={1} flexShrink={0} className={classes.channelSelect}>
            <Dropdown
              addWorkspace
              data={groups}
              translate={currentTeam.msGroups}
              type="teams"
              setValue={onChange}
              value={selectedGroup}
              autocomplete
              defaultValue={groupId}
            />
          </Box>
        ) : null}
        <Box p={1} flexShrink={0} className={classes.channelSelect}>
          <Dropdown
            addWorkspace
            data={dropdownChannels}
            translate={currentTeam.teamId ? currentTeam.channels : currentTeam.msChannels}
            type="channels"
            setValue={onChange}
            value={selectedChannel}
            autocomplete
            defaultValue={channelId}
          />
        </Box>
      </Box>
      {!loading ? (
        <>
          {activityReport &&
          activityReport.channelActivity &&
          activityReport.channelActivity.length === 0 ? (
            <div className={classes.noData}>
              <Typography component="h2">No data to be displayed in the last 24 hours!</Typography>
              <EmptyTrunk animationWidth={400} animationHeight={400} containerHeight="initial" />
            </div>
          ) : (
            <>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={2}>
                  <ActivityCounter activityReport={activityReport} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ChannelsCounter activityReport={activityReport} currentTeam={currentTeam} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ActiveMembersCounter
                    activityReport={activityReport}
                    currentTeam={currentTeam}
                    channel={channel}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <MentionsCounter activityReport={activityReport} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <MoodCounter activityReport={activityReport} />
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Paper className={classes.metricBoxChart}>
                    <Typography component="h2" className={classes.subtitle}>
                      Most engaged users
                    </Typography>
                    <div className={classes.listView}>
                      {mostActiveMembers.map((member: any, index: number) => (
                        <ActivityList
                          key={`activity-list-${index}`}
                          data={member}
                          translate={
                            currentTeam.teamId ? currentTeam.members : currentTeam.msMembers
                          }
                          type="memberActivity"
                        />
                      ))}
                    </div>
                  </Paper>
                </Grid>
                {channel === '' ? (
                  <Grid item xs={12} md={4}>
                    <Paper className={classes.metricBoxChart}>
                      <Typography component="h2" className={classes.subtitle}>
                        Most Active Channels
                      </Typography>
                      <div className={classes.listView}>
                        {mostActiveChannels.map((channel: any) => (
                          <ActivityList
                            key={channel.id}
                            data={channel}
                            translate={
                              currentTeam.teamId ? currentTeam.channels : currentTeam.msChannels
                            }
                            type="channelActivity"
                          />
                        ))}
                      </div>
                    </Paper>
                  </Grid>
                ) : (
                  <Grid item xs={12} md={4}>
                    <Paper className={classes.metricBoxChart}>
                      <Typography component="h2" className={classes.subtitle}>
                        Members without Activity
                      </Typography>
                      <div className={classes.listView}>
                        {membersWithoutActivity.map((member: any) => (
                          <ActivityList
                            key={member.id}
                            data={member}
                            translate={
                              currentTeam.teamId ? currentTeam.members : currentTeam.msMembers
                            }
                            type="memberActivity"
                            hideMood={true}
                          />
                        ))}
                      </div>
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12} md={4}>
                  <Paper elevation={1} className={clsx(classes.metricBoxChart)}>
                    <Typography component="h2" className={classes.subtitle}>
                      Low mood avg
                    </Typography>
                    <div
                      className={classes.listView}
                      style={
                        !mostProblematicMembers.filter((mem: any) => mem.score < 0.333).length
                          ? { display: 'flex', justifyContent: 'center' }
                          : {}
                      }
                    >
                      {mostProblematicMembers.filter((mem: any) => mem.score < 0.333).length ? (
                        mostProblematicMembers
                          .filter((mem: any) => mem.score < 0.333)
                          .map((member: any) => (
                            <ActivityList
                              key={member.id}
                              data={member}
                              translate={
                                currentTeam.teamId ? currentTeam.members : currentTeam.msMembers
                              }
                              type="memberActivity"
                            />
                          ))
                      ) : (
                        <Alert severity="success">
                          No team member showed low mood... Excellent!
                        </Alert>
                      )}
                    </div>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </>
      ) : (
        <div className={classes.loading}>
          <Loading containerHeight="70vh" />
        </div>
      )}
    </div>
  );
};

export { Summary };
